import {
  daysAgo,
  isSiteRole,
  REQ_STAGE,
  roDate,
  siteLabel,
  specsText,
  todayIso,
  type ReqStage,
  type Requisition,
  t,
  intlLocale,
} from '@procumi/shared';
import { useState } from 'react';
import { Dialog } from '../components/Dialog';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { ask } from '../lib/confirm';
import { currentUser } from '../lib/me';
import type { Route } from '../routes';

/** Drumul obișnuit al unui necesar, ca omul de pe șantier să vadă cât mai e. */
const TRACK: { stages: ReqStage[]; label: string }[] = [
  { stages: ['open'], label: t('Trimis') },
  { stages: ['quoting'], label: t('Se cer oferte') },
  { stages: ['ordering'], label: t('Se comandă') },
  { stages: ['ordered', 'partial'], label: t('Comandat') },
  { stages: ['delivered'], label: t('Livrat') },
];

const qty = (n: number) => n.toLocaleString(intlLocale(), { maximumFractionDigits: 3 });

function Track({ r }: { r: Requisition }) {
  const at = TRACK.findIndex((st) => st.stages.includes(r.stage));
  if (at < 0) return null;
  return (
    <ol className="req-track" aria-label={t('Unde a ajuns necesarul')}>
      {TRACK.map((st, i) => (
        <li
          key={st.label}
          className={i < at ? 'done' : i === at ? 'now' : undefined}
          aria-current={i === at ? 'step' : undefined}
        >
          <span className="dot" aria-hidden="true" />
          <span className="lbl">{i === at && r.stage === 'partial' ? t('Livrat parțial') : st.label}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Un necesar de pe șantier. Omul care l-a trimis îi vede drumul până la livrare;
 * achizițiile fac din el o cerere de ofertă sau îl resping, cu motiv.
 */
export function RequisitionPage({ id, go, toast }: { id: string; go: (r: Route) => void; toast: Toast }) {
  const {
    data: r,
    setData,
    error,
    loading,
    reload,
  } = useAsync(() => api.requisition(id), [id], `requisition:${id}`);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const me = currentUser();
  const buyer = !isSiteRole(me.role);

  if (loading) return <Loading />;
  if (error || !r) return <ErrorBox error={error ?? t('Necesarul nu a fost găsit.')} onRetry={reload} />;

  const mine = r.createdBy?.id === me.id;
  const st = REQ_STAGE[r.stage];
  const late = r.neededBy && r.neededBy < todayIso() && ['open', 'quoting', 'ordering'].includes(r.stage);

  const act = async (fn: () => Promise<Requisition>, done: string) => {
    setBusy(true);
    try {
      setData(await fn());
      toast(done);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!reason.trim()) return toast(t('Scrie motivul: omul de pe șantier îl primește pe mail'));
    await act(() => api.rejectRequisition(r.id, reason.trim()), t('{0} a fost respins', r.number));
    setRejecting(false);
  };

  const cancel = async () => {
    const ok = await ask({
      title: t('Retragi necesarul {0}?', r.number),
      message: t('Achizițiile nu-l mai văd de preluat. Dacă tot trebuie, faci altul.'),
      confirm: t('Retrage necesarul'),
      danger: true,
    });
    if (ok) await act(() => api.cancelRequisition(r.id), t('{0} a fost retras', r.number));
  };

  return (
    <>
      <div className="head">
        <div>
          <button className="back" onClick={() => go({ view: 'requisitions' })}>
            {t('‹ Tot necesarul')}
          </button>
          <h1>{r.title}</h1>
          <p>
            {t(
              '{0} · {1}{2} · trimis de {3}, {4}',
              r.number,
              r.category ? `${r.category} · ` : '',
              siteLabel(r.site),
              r.createdBy?.name ?? t('cineva care nu mai e în firmă'),
              daysAgo(r.createdAt),
            )}
          </p>
        </div>
        <div className="acts">
          {buyer && r.status === 'open' ? (
            <>
              <button className="btn" disabled={busy} onClick={() => setRejecting(true)}>
                {t('Respinge')}
              </button>
              <button
                className="btn primary"
                disabled={busy}
                onClick={() => go({ view: 'new', requisitionIds: [r.id] })}
              >
                {t('Fă cerere de ofertă')}
              </button>
            </>
          ) : null}
          {buyer && r.status === 'rejected' ? (
            <button
              className="btn"
              disabled={busy}
              onClick={() => void act(() => api.reopenRequisition(r.id), t('{0} așteaptă din nou', r.number))}
            >
              {t('Redeschide')}
            </button>
          ) : null}
          {(mine || buyer) && r.status === 'open' ? (
            <button
              className="btn"
              disabled={busy}
              onClick={() => go({ view: 'newRequisition', editId: r.id })}
            >
              {t('Editează')}
            </button>
          ) : null}
          {mine && r.status === 'open' ? (
            <button className="btn" disabled={busy} onClick={() => void cancel()}>
              {t('Retrage necesarul')}
            </button>
          ) : null}
        </div>
      </div>

      <section className="sheet pad req-status">
        <div className="req-status-head">
          <span className={`pill ${st.pill}`}>{st.label}</span>
          <span className="muted">{t(st.hint)}</span>
        </div>
        <Track r={r} />
        <dl className="req-facts">
          <div>
            <dt>{t('Trebuie pe șantier')}</dt>
            <dd className={late ? 'late-text' : undefined}>
              {r.neededBy ? roDate(r.neededBy) : t('nu s-a spus')}
              {late ? t(' · a trecut') : ''}
            </dd>
          </div>
          {r.deliveryDate ? (
            <div>
              <dt>{t('Livrare promisă')}</dt>
              <dd>{roDate(r.deliveryDate)}</dd>
            </div>
          ) : null}
          {r.decidedBy && r.status !== 'canceled' ? (
            <div>
              <dt>{r.status === 'rejected' ? t('Respins de') : t('Preluat de')}</dt>
              <dd>
                {r.decidedBy}
                {r.decidedAt ? `, ${daysAgo(r.decidedAt)}` : ''}
              </dd>
            </div>
          ) : null}
          {r.rfq && buyer ? (
            <div>
              <dt>{t('Cererea de ofertă')}</dt>
              <dd>
                <button type="button" className="link" onClick={() => go({ view: 'rfq', id: r.rfq!.id })}>
                  {r.rfq.title}
                </button>
              </dd>
            </div>
          ) : null}
        </dl>
        {r.status === 'rejected' && r.reason ? (
          <p className="req-reason">
            <b>{t('Motivul:')}</b> {r.reason}
          </p>
        ) : null}
      </section>

      <section className="sheet table-wrap" style={{ marginTop: 'var(--space-6)' }}>
        <table className="data-table req-items">
          <thead>
            <tr>
              <th style={{ width: 48 }}>{t('Nr.')}</th>
              <th>{t('Material')}</th>
              <th className="num">{t('Cantitate')}</th>
            </tr>
          </thead>
          <tbody>
            {r.items.map((it, k) => (
              <tr key={k} className="static-row">
                <td data-label={t('Nr.')} className="req-no">
                  {k + 1}
                </td>
                <td data-label={t('Material')}>
                  {it.desc}
                  {it.rentDays || specsText(it.specs) ? (
                    <small>
                      {[
                        specsText(it.specs),
                        it.rentDays ? `chirie ${it.rentDays} ${it.rentDays === 1 ? t('zi') : t('zile')}` : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  ) : null}
                </td>
                <td data-label={t('Cantitate')} className="num">
                  {qty(it.qty)} {it.unit}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {r.note ? (
        <section className="sheet pad" style={{ marginTop: 'var(--space-6)' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>{t('Pentru achiziții')}</h2>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{r.note}</p>
        </section>
      ) : null}

      {rejecting ? (
        <Dialog onClose={() => setRejecting(false)} dirty={!!reason.trim()}>
          <div className="dlg-h">
            <h2>{t('Respingi {0}?', r.number)}</h2>
            <p className="hint">
              {t(
                '{0} primește motivul pe mail și îl vede în aplicație.',
                r.createdBy?.name ?? t('Omul de pe șantier'),
              )}
            </p>
          </div>
          <div className="dlg-b">
            <label className="f" htmlFor="req-reason">
              {t('Motivul')}
              <textarea
                id="req-reason"
                className="inp"
                rows={3}
                maxLength={500}
                value={reason}
                placeholder={t('ex. Avem pe stoc în depozit, le aduce Mihai joi')}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          </div>
          <div className="dlg-f">
            <button className="btn" onClick={() => setRejecting(false)}>
              {t('Renunță')}
            </button>
            <button className="btn danger" disabled={busy} onClick={() => void reject()}>
              {t('Respinge necesarul')}
            </button>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
